
import React, { useState, useEffect, useCallback } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { WorldStateProvider, useWorldState } from "../components/state/WorldStateContext.jsx";
import { AuthProvider, AuthGuard } from "../components/auth";
import GameLoopEngine from "../components/sim/GameLoopEngine.jsx";
import CheatConsole from "../components/dev/CheatConsole.jsx";
// import IntroVideo from "../components/IntroVideo.jsx"; // REMOVED - no intro video
import { FlightToastProvider } from "../components/ui/FlightToast.jsx";
import FlightCompletionListener from "../components/sim/FlightCompletionListener.jsx";
import FuelEmergencyListener from "../components/sim/FuelEmergencyListener.jsx";
import { RadioProvider } from "../components/ui/RadioPlayer.jsx";
import { VerticalNav } from "../components/VerticalNav.jsx";
import PauseMenu from "../components/PauseMenu.jsx";

const queryClient = new QueryClient();

// Routes where nav should NOT show (setup/onboarding flow)
const SETUP_ROUTES = ['/livery', '/mode-select', '/MainMenu', '/Start', '/VideoIntro', '/IntroCinematic', '/'];

// Inner layout component that can access WorldState for nav
function LayoutContent({ children }) {
  const { worldState } = useWorldState();
  const location = useLocation();
  const gameMode = worldState?.gameMode;
  const [isPaused, setIsPaused] = useState(false);

  // Only show nav after game starts AND not on setup routes
  const hasGameStarted = worldState?.airline?.name;
  const isSetupRoute = SETUP_ROUTES.includes(location.pathname);
  const showNav = hasGameStarted && !isSetupRoute;

  // ESC key to toggle pause menu (only when in game, not on setup screens)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && hasGameStarted && !isSetupRoute) {
        // Don't toggle if cheat console is open (backtick)
        const cheatConsoleOpen = document.querySelector('[data-cheat-console="open"]');
        if (!cheatConsoleOpen) {
          setIsPaused(prev => !prev);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasGameStarted, isSetupRoute]);

  const handleClosePause = useCallback(() => {
    setIsPaused(false);
  }, []);

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      {/* Persistent Nav - only show after game starts and not on setup screens */}
      {showNav && <VerticalNav gameMode={gameMode} />}
      {/* Main Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {children || <Outlet />}
      </div>
      {/* Pause Menu - ESC to toggle */}
      <PauseMenu isOpen={isPaused} onClose={handleClosePause} />
    </div>
  );
}

// Theme CSS variables
const themeStyles = {
  // Core backgrounds
  "--ops-bg": "#0a0a0a",
  "--ops-bg-alt": "#111111",
  "--ops-bg-panel": "#0c0c0c",
  "--ops-accent-dark": "#0d1f1a",

  // Text colors
  "--ops-text-main": "#e0e0e0",
  "--ops-text-dim": "#666666",
  "--ops-radar-green": "#60ffd0",

  // Status colors
  "--ops-good": "#22c55e",
  "--ops-warn": "#eab308",
  "--ops-alert-red": "#ef4444",
  "--ops-alert-red-dim": "#7f1d1d",

  // Borders & accents
  "--ops-border": "#1a1a1a",
  "--neon-glow": "rgba(96, 255, 208, 0.5)",
};

export default function Layout({ children }) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WorldStateProvider>
          <AuthGuard>
            <RadioProvider>
              <FlightToastProvider>
                <div style={themeStyles} className="min-h-screen">
                  {/* Game Loop runs in background */}
                  <GameLoopEngine />
                  {/* Flight completion listener - triggers toasts */}
                  <FlightCompletionListener />
                  {/* Fuel emergency listener - shows emergency modal */}
                  <FuelEmergencyListener />
                  {/* Layout with persistent nav */}
                  <LayoutContent>{children}</LayoutContent>
                  {/* Cheat Console - Global, press ` to toggle */}
                  <CheatConsole />
                </div>
              </FlightToastProvider>
            </RadioProvider>
          </AuthGuard>
        </WorldStateProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
